/**
 * What: Offers an optional meeting with the TestFeed team from Shopify Home.
 * Transport / data-flow: Opens Calendly in a nested frame and provides the same hosted URL as a fallback.
 * Invariants: Opening the calendar is not a booking, and neither booking nor calendar availability gates Home.
 */
import { useState } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { ShopifyAppBridge } from '../../../shopifyApiFetch'
import { buildCalendlyBookingUrl } from '../../../resultsNavigation'
import { trackShopifyAction } from '../../../shopifyUiTracking'

const MODAL_ID = 'home-onboarding-call'

/** Loads the external calendar only after the merchant chooses to open it. */
export function OnboardingCall({ shopify, compact = false }: { shopify: ShopifyAppBridge; compact?: boolean }) {
  const [opened, setOpened] = useState(false)
  const copy = shopifyComponentCopy.appHome
  const url = buildCalendlyBookingUrl({ source: 'home' })
  const calendar = opened ? (
    <iframe title={copy.onboardingCallHeading} src={url} style={{ width: '100%', height: '700px', border: 0 }} />
  ) : null

  return (
    <>
      <s-box padding={compact ? 'none' : 'base'}>
        <s-stack gap="base">
          {!compact && <s-heading>{copy.onboardingCallHeading}</s-heading>}
          <s-paragraph>{copy.onboardingCallBody}</s-paragraph>
          <s-button
            commandFor={MODAL_ID}
            onClick={() => trackShopifyAction(shopify, 'open-info', () => setOpened(true))}
          >
            {copy.onboardingCallAction}
          </s-button>
        </s-stack>
      </s-box>
      <s-modal id={MODAL_ID} heading={copy.onboardingCallHeading} size="large">
        <s-stack gap="base">
          {calendar}
          <s-link
            href={url}
            target="_blank"
            onClick={() => trackShopifyAction(shopify, 'open-booking', () => undefined)}
          >
            {copy.onboardingCallExternal}
          </s-link>
        </s-stack>
        <s-button slot="secondary-actions" commandFor={MODAL_ID} command="--hide">
          {copy.onboardingCallClose}
        </s-button>
      </s-modal>
    </>
  )
}

/** Optional guide row; opening it does not change setup progress. */
export function OnboardingCallStep({
  shopify,
  open,
  onToggle,
}: {
  shopify: ShopifyAppBridge
  open: boolean
  onToggle: () => void
}) {
  const copy = shopifyComponentCopy.appHome
  return (
    <>
      <s-clickable padding="small" onClick={onToggle} accessibilityLabel={copy.onboardingCallHeading}>
        <s-grid gridTemplateColumns="auto 1fr auto" gap="small" alignItems="center">
          <s-icon type="calendar" tone="neutral"></s-icon>
          <s-text>{copy.onboardingCallHeading}</s-text>
          <s-icon type={open ? 'chevron-up' : 'chevron-down'} tone="neutral"></s-icon>
        </s-grid>
      </s-clickable>
      <s-box padding="small" paddingBlockStart="none" display={open ? 'auto' : 'none'}>
        <s-box padding="base" background="subdued" borderRadius="base">
          <OnboardingCall shopify={shopify} compact />
        </s-box>
      </s-box>
    </>
  )
}
